import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { supabase } from '../../utils/supabaseClient';
import * as XLSX from 'xlsx';
import pdfMake from 'pdfmake/build/pdfmake';

// Setup pdfMake fonts (optional - will use default fonts if not available)
try {
  const pdfFonts = require('pdfmake/build/vfs_fonts');
  if (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
    pdfMake.vfs = pdfFonts.pdfMake.vfs;
  }
} catch (error) {
  console.warn('PDF fonts not available, using default fonts');
}

const AppointmentAdminDashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [permitTypeFilter, setPermitTypeFilter] = useState('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const permitTypes = [
    { value: 'firearm', label: 'อาวุธปืน' },
    { value: 'hotel', label: 'โรงแรม' },
    { value: 'gambling', label: 'การพนัน' }
  ];

  const statusOptions = [
    { value: 'confirmed', label: 'ยืนยันแล้ว' },
    { value: 'approved', label: 'อนุมัติแล้ว' },
    { value: 'cancelled', label: 'ยกเลิก' },
    { value: 'completed', label: 'เสร็จสิ้น' }
  ];

  // ดึงข้อมูลการจองคิว
  const fetchAppointments = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('appointments')
        .select('*')
        .eq('appointment_date', selectedDate)
        .order('time_slot', { ascending: true });

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching appointments:', error);
        return;
      }

      setAppointments(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, [selectedDate]);

  // กรองข้อมูลตามสถานะและประเภท
  const filteredAppointments = appointments.filter(appointment => {
    const statusMatch = statusFilter === 'all' || appointment.status === statusFilter;
    const typeMatch = permitTypeFilter === 'all' || appointment.permit_type === permitTypeFilter;
    return statusMatch && typeMatch;
  });

  // อัปเดตสถานะการจองคิว
  const updateAppointmentStatus = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) {
        console.error('Error updating status:', error);
        return;
      }

      // รีเฟรชข้อมูล
      fetchAppointments();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // ดูรายละเอียดการจองคิว
  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedAppointment(null);
  };

  // ส่งออก Excel
  const exportToExcel = () => {
    const data = filteredAppointments.map(appointment => ({
      'เลขที่จอง': appointment.booking_number,
      'ชื่อ-สกุล': appointment.citizen_name,
      'เลขบัตรประชาชน': appointment.citizen_id,
      'เบอร์โทร': appointment.phone_number,
      'ประเภท': getPermitTypeLabel(appointment.permit_type),
      'วันที่': new Date(appointment.appointment_date).toLocaleDateString('th-TH'),
      'เวลา': appointment.time_slot,
      'สถานะ': getStatusLabel(appointment.status),
      'วันที่จอง': new Date(appointment.created_at).toLocaleString('th-TH')
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'รายการจองคิว');
    
    const fileName = `รายการจองคิว_${selectedDate}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ส่งออก PDF
  const exportToPDF = () => {
    const docDefinition = {
      content: [
        {
          text: 'รายการจองคิวงานขออนุญาตฝ่ายปกครอง',
          style: 'header',
          alignment: 'center'
        },
        {
          text: `วันที่: ${new Date(selectedDate).toLocaleDateString('th-TH')}`,
          style: 'subheader',
          alignment: 'center',
          margin: [0, 0, 0, 20]
        },
        {
          table: {
            headerRows: 1,
            widths: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'เลขที่จอง', style: 'tableHeader' },
                { text: 'ชื่อ-สกุล', style: 'tableHeader' },
                { text: 'เบอร์โทร', style: 'tableHeader' },
                { text: 'ประเภท', style: 'tableHeader' },
                { text: 'เวลา', style: 'tableHeader' },
                { text: 'สถานะ', style: 'tableHeader' },
                { text: 'วันที่จอง', style: 'tableHeader' }
              ],
              ...filteredAppointments.map(appointment => [
                appointment.booking_number,
                appointment.citizen_name,
                appointment.phone_number,
                getPermitTypeLabel(appointment.permit_type),
                appointment.time_slot,
                getStatusLabel(appointment.status),
                new Date(appointment.created_at).toLocaleDateString('th-TH')
              ])
            ]
          }
        }
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 10]
        },
        subheader: {
          fontSize: 14,
          margin: [0, 0, 0, 10]
        },
        tableHeader: {
          bold: true,
          fontSize: 10,
          color: 'black',
          fillColor: '#f0f0f0'
        }
      },
      defaultStyle: {
        fontSize: 9
      }
    };

    const fileName = `รายการจองคิว_${selectedDate}.pdf`;
    pdfMake.createPdf(docDefinition).download(fileName);
  };

  const getPermitTypeLabel = (type) => {
    const permit = permitTypes.find(p => p.value === type);
    return permit ? permit.label : type;
  };

  const getStatusLabel = (status) => {
    const statusOption = statusOptions.find(s => s.value === status);
    return statusOption ? statusOption.label : status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-blue-100 text-blue-700';
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      case 'completed':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <BreadcrumbTrail />
        
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={24} color="white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                แดชบอร์ดจัดการจองคิว
              </h1>
              <p className="text-muted-foreground mt-1">
                จัดการการจองคิวงานขออนุญาตฝ่ายปกครอง
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card rounded-lg border border-border shadow-government p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                เลือกวันที่
              </label>
              <input
                type="date"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary/60"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                สถานะ
              </label>
              <select
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary/60"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">ทั้งหมด</option>
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                ประเภท
              </label>
              <select
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary/60"
                value={permitTypeFilter}
                onChange={(e) => setPermitTypeFilter(e.target.value)}
              >
                <option value="all">ทั้งหมด</option>
                {permitTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end space-x-2">
              <Button
                variant="outline"
                onClick={exportToExcel}
                iconName="FileSpreadsheet"
                iconPosition="left"
                className="flex-1"
              >
                Excel
              </Button>
              <Button
                variant="outline"
                onClick={exportToPDF}
                iconName="FileText"
                iconPosition="left"
                className="flex-1"
              >
                PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Appointments Table */}
        <div className="bg-card rounded-lg border border-border shadow-government">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">
                รายการจองคิวประจำวันที่ {new Date(selectedDate).toLocaleDateString('th-TH')}
              </h2>
              <div className="text-sm text-muted-foreground">
                ทั้งหมด {filteredAppointments.length} รายการ
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center space-x-2 text-muted-foreground">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span>กำลังโหลดข้อมูล...</span>
                </div>
              </div>
            ) : filteredAppointments.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">ไม่พบข้อมูลการจองคิวในวันที่เลือก</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 font-medium text-foreground">เลขที่จอง</th>
                      <th className="text-left py-3 px-2 font-medium text-foreground">ชื่อ-สกุล</th>
                      <th className="text-left py-3 px-2 font-medium text-foreground">เบอร์โทร</th>
                      <th className="text-left py-3 px-2 font-medium text-foreground">ประเภท</th>
                      <th className="text-left py-3 px-2 font-medium text-foreground">เวลา</th>
                      <th className="text-left py-3 px-2 font-medium text-foreground">สถานะ</th>
                      <th className="text-left py-3 px-2 font-medium text-foreground">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppointments.map((appointment) => (
                      <tr key={appointment.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-3 px-2 font-mono text-xs">{appointment.booking_number}</td>
                        <td className="py-3 px-2">{appointment.citizen_name}</td>
                        <td className="py-3 px-2">{appointment.phone_number}</td>
                        <td className="py-3 px-2">
                          <span className="px-2 py-1 bg-accent/20 text-accent-foreground rounded text-xs">
                            {getPermitTypeLabel(appointment.permit_type)}
                          </span>
                        </td>
                        <td className="py-3 px-2">{appointment.time_slot}</td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(appointment.status)}`}>
                            {getStatusLabel(appointment.status)}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewDetails(appointment)}
                            >
                              <Icon name="Eye" size={14} className="mr-1" />
                              ดูรายละเอียด
                            </Button>
                            <select
                              className="text-xs border border-border rounded px-2 py-1 bg-background"
                              value={appointment.status}
                              onChange={(e) => updateAppointmentStatus(appointment.id, e.target.value)}
                            >
                              {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Detail Modal */}
      {showDetailModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-foreground">
                  รายละเอียดการจองคิว
                </h3>
                <Button variant="outline" onClick={closeDetailModal}>
                  <Icon name="X" size={16} />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      เลขที่จอง
                    </label>
                    <p className="text-sm font-mono bg-muted/30 p-2 rounded">
                      {selectedAppointment.booking_number}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      สถานะ
                    </label>
                    <span className={`inline-block px-2 py-1 rounded text-xs ${getStatusColor(selectedAppointment.status)}`}>
                      {getStatusLabel(selectedAppointment.status)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      ชื่อ-สกุล
                    </label>
                    <p className="text-sm">{selectedAppointment.citizen_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      เลขบัตรประชาชน
                    </label>
                    <p className="text-sm font-mono">{selectedAppointment.citizen_id}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      เบอร์โทรศัพท์
                    </label>
                    <p className="text-sm">{selectedAppointment.phone_number}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      ประเภทการขออนุญาต
                    </label>
                    <p className="text-sm">{getPermitTypeLabel(selectedAppointment.permit_type)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      วันที่นัดหมาย
                    </label>
                    <p className="text-sm">{new Date(selectedAppointment.appointment_date).toLocaleDateString('th-TH')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      ช่วงเวลา
                    </label>
                    <p className="text-sm">{selectedAppointment.time_slot}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    วันที่จอง
                  </label>
                  <p className="text-sm">{new Date(selectedAppointment.created_at).toLocaleString('th-TH')}</p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-border mt-6">
                <Button variant="outline" onClick={closeDetailModal}>
                  ปิด
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentAdminDashboard;
